// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Button,
  Group,
  Modal,
  Select,
  Stack,
  TextInput,
  Text,
  ActionIcon,
  Tooltip,
  Paper,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconLock,
  IconLockOpen,
  IconCheck,
  IconHistory,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import {
  usePayPeriods,
  useCreatePayPeriod,
  useTransitionPayPeriod,
} from '../../hooks/usePayPeriods';

// ── State machine ───────────────────────────────────────────────────
// OPEN -> CLOSED -> POSTED
//         CLOSED -> REOPENED -> CLOSED (re-enter cycle)

const STATUS_COLOR_MAP: Record<string, string> = {
  open: 'green',
  closed: 'yellow',
  posted: 'blue',
  reopened: 'orange',
};

/** Returns allowed target states for a given current status. */
function allowedTransitions(status: string): { value: string; label: string; icon: React.ReactNode }[] {
  switch (status.toLowerCase()) {
    case 'open':
      return [{ value: 'CLOSED', label: 'Close Period', icon: <IconLock size={14} /> }];
    case 'closed':
      return [
        { value: 'POSTED', label: 'Post Period', icon: <IconCheck size={14} /> },
        { value: 'REOPENED', label: 'Reopen Period', icon: <IconLockOpen size={14} /> },
      ];
    case 'reopened':
      return [{ value: 'CLOSED', label: 'Close Period', icon: <IconLock size={14} /> }];
    default:
      return [];
  }
}

interface PayPeriodRow extends Record<string, unknown> {
  id: string;
  startDate: string;
  endDate: string;
  payDate?: string;
  frequency?: string;
  status: string;
  version?: number;
  transactionDate?: string;
}

export default function PayPeriodListPage() {
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState<{
    id: string;
    targetState: string;
    label: string;
  } | null>(null);
  const [transitionReason, setTransitionReason] = useState('');

  const { data, loading: isLoading } = usePayPeriods({ page, perPage: 25 });
  const createMutation = useCreatePayPeriod();
  const transitionMutation = useTransitionPayPeriod();

  const form = useForm({
    initialValues: {
      startDate: '',
      endDate: '',
      payDate: '',
      frequency: 'BIWEEKLY',
    },
    validate: {
      startDate: (v: string) => (v ? null : 'Start date is required'),
      endDate: (v: string, values: { startDate: string }) => {
        if (!v) return 'End date is required';
        if (values.startDate && v <= values.startDate) return 'End date must be after start date';
        return null;
      },
      payDate: (v: string, values: { endDate: string }) => {
        if (!v) return 'Pay date is required';
        if (values.endDate && v < values.endDate) return 'Pay date must be on or after end date';
        return null;
      },
      frequency: (v: string) => (v ? null : 'Frequency is required'),
    },
  });

  const handleCreate = form.onSubmit((values) => {
    createMutation.mutate(values, {
      onSuccess: () => {
        notifications.show({
          title: 'Pay Period Created',
          message: `New ${values.frequency.toLowerCase()} pay period created (append-only v1).`,
          color: 'green',
        });
        setCreateOpen(false);
        form.reset();
      },
      onError: (err: any) => {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to create pay period',
          color: 'red',
        });
      },
    });
  });

  const handleTransition = () => {
    if (!transitionTarget) return;
    transitionMutation.mutate(
      {
        id: transitionTarget.id,
        targetState: transitionTarget.targetState,
        reason: transitionReason || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Status Updated',
            message: `Pay period transitioned to ${transitionTarget.targetState} (new version created).`,
            color: 'green',
          });
          setTransitionTarget(null);
          setTransitionReason('');
        },
        onError: (err: any) => {
          notifications.show({
            title: 'Transition Failed',
            message: err instanceof Error ? err.message : 'Failed to transition pay period',
            color: 'red',
          });
        },
      },
    );
  };

  const columns: Column<PayPeriodRow>[] = [
    {
      key: 'startDate',
      label: 'Start Date',
      sortable: true,
      width: 120,
      render: (row) => new Date(row.startDate).toLocaleDateString(),
    },
    {
      key: 'endDate',
      label: 'End Date',
      sortable: true,
      width: 120,
      render: (row) => new Date(row.endDate).toLocaleDateString(),
    },
    {
      key: 'payDate',
      label: 'Pay Date',
      width: 120,
      render: (row) => (row.payDate ? new Date(row.payDate).toLocaleDateString() : '-'),
    },
    {
      key: 'frequency',
      label: 'Frequency',
      width: 120,
      render: (row) => {
        const labels: Record<string, string> = {
          WEEKLY: 'Weekly',
          BIWEEKLY: 'Bi-Weekly',
          SEMIMONTHLY: 'Semi-Monthly',
          MONTHLY: 'Monthly',
          QUARTERLY: 'Quarterly',
          ANNUAL: 'Annual',
        };
        return labels[row.frequency ?? ''] ?? row.frequency ?? '-';
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (row) => <StatusBadge status={row.status} colorMap={STATUS_COLOR_MAP} />,
    },
    {
      key: 'version',
      label: 'Version',
      width: 80,
      render: (row) => (
        <Group gap={4}>
          <IconHistory size={14} color="var(--mantine-color-dimmed)" />
          <Text size="sm" c="dimmed">
            v{row.version ?? 1}
          </Text>
        </Group>
      ),
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Pay Periods"
        subtitle={data ? `${data.length} pay periods` : undefined}
        breadcrumbs={[{ label: 'Payroll', to: '/admin/workforce/payroll' }, { label: 'Pay Periods' }]}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
            New Pay Period
          </Button>
        }
      />

      <DataTable<PayPeriodRow>
        columns={columns}
        data={(data as PayPeriodRow[]) ?? []}
        loading={isLoading}
        pagination={
          data
            ? {
                page,
                pageSize: 25,
                total: data.length,
                onPageChange: setPage,
              }
            : undefined
        }
        getRowKey={(row) => row.id}
        emptyMessage="No pay periods found. Create one to get started."
        actions={(row) => {
          const transitions = allowedTransitions(row.status);
          if (transitions.length === 0) return null;
          return (
            <Group gap={4}>
              {transitions.map((t) => (
                <Tooltip key={t.value} label={t.label}>
                  <ActionIcon
                    variant="light"
                    size="sm"
                    color={t.value === 'POSTED' ? 'blue' : t.value === 'REOPENED' ? 'orange' : 'gray'}
                    onClick={() =>
                      setTransitionTarget({ id: row.id, targetState: t.value, label: t.label })
                    }
                  >
                    {t.icon}
                  </ActionIcon>
                </Tooltip>
              ))}
            </Group>
          );
        }}
      />

      {/* ── Create Pay Period Modal ──────────────────────────────────── */}
      <Modal
        opened={createOpen}
        onClose={() => {
          setCreateOpen(false);
          form.reset();
        }}
        title="Create Pay Period"
        size="md"
      >
        <form onSubmit={handleCreate}>
          <Stack gap="md">
            <TextInput
              label="Start Date"
              type="date"
              required
              {...form.getInputProps('startDate')}
            />
            <TextInput
              label="End Date"
              type="date"
              required
              {...form.getInputProps('endDate')}
            />
            <TextInput
              label="Pay Date"
              type="date"
              required
              {...form.getInputProps('payDate')}
            />
            <Select
              label="Frequency"
              required
              data={[
                { value: 'WEEKLY', label: 'Weekly' },
                { value: 'BIWEEKLY', label: 'Bi-Weekly' },
                { value: 'SEMIMONTHLY', label: 'Semi-Monthly' },
                { value: 'MONTHLY', label: 'Monthly' },
                { value: 'QUARTERLY', label: 'Quarterly' },
                { value: 'ANNUAL', label: 'Annual' },
              ]}
              {...form.getInputProps('frequency')}
            />
            <Paper p="sm" bg="blue.0" radius="sm">
              <Text size="xs" c="blue.7">
                Append-only: Creating a pay period produces version 1. Each status transition
                creates a new immutable version.
              </Text>
            </Paper>
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setCreateOpen(false);
                  form.reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={createMutation.isPending}>
                Create Pay Period
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* ── Transition Confirmation Modal ────────────────────────────── */}
      <Modal
        opened={transitionTarget !== null}
        onClose={() => {
          setTransitionTarget(null);
          setTransitionReason('');
        }}
        title={transitionTarget?.label ?? 'Confirm Transition'}
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will transition the pay period to{' '}
            <Text span fw={600}>
              {transitionTarget?.targetState}
            </Text>
            . A new immutable version will be created (append-only).
          </Text>
          <TextInput
            label="Reason (optional)"
            placeholder="Why is this transition happening?"
            value={transitionReason}
            onChange={(e) => setTransitionReason(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setTransitionTarget(null);
                setTransitionReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              color={transitionTarget?.targetState === 'REOPENED' ? 'orange' : 'blue'}
              loading={transitionMutation.isPending}
              onClick={handleTransition}
            >
              {transitionTarget?.label ?? 'Confirm'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
